import { Switch, Route } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthenticatedTemplate, UnauthenticatedTemplate, useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useEffect, useState } from "react";
import { Provider, useDispatch, useSelector } from "react-redux";
import { Toaster } from "./components/ui/toaster";
import ErrorBoundary from "./components/ui/ErrorBoundary";
import Sidebar from "./components/layout/Sidebar";
import Header from "./components/layout/Header";
import AuthorizedRoute from "./components/auth/AuthorizedRoute";
import AccessDenied from "./components/auth/AccessDenied";
import PostCallDocumentation from "./Pages/PostCallDocumentation";
import Dashboard from "./Pages/Dashboard";
import IntelligenceDashboard from "./features/intelligence/IntelligenceDashboard";
import Appointments from "./Pages/Appointments";
import Patients from "./Pages/Patients";
import PatientReports from "./Pages/PatientReports";
import Reports from "./Pages/Reports";
import BillingReports from "./Pages/BillingReports";
import BillingHistory from "./Pages/BillingHistory";
import BillCalculation from "./Pages/BillCalculation";
import InvoicePreview from "./Pages/InvoicePreview";
import Settings from "./Pages/Settings";
import ProfileSettings from "./Pages/ProfileSettings";
import AthenaIntegration from "./Pages/AthenaIntegration";
import PaymentBilling from "./Pages/PaymentBilling";
import PaymentMethodManagement from "./Pages/PaymentMethodManagement";
import SubscriptionPlans from "./Pages/SubscriptionPlans";
import RBACManagement from "./Pages/RBACManagement";
import NotFound from "./Pages/not-found";
import VideoRecorder from "./Pages/VideoRecorder";
import AboutUs from "./Pages/AboutUs";
import AboutSeismic from "./Pages/AboutSeismic";
import Connect from "./Pages/Connect";
import ContactUs from "./Pages/ContactUs";
import Documentation from "./Pages/Documentation";
import AuthPage from "./Pages/AuthPage";
import StreamVideoCoreV3 from "./Pages/StreamVideoCoreV3";
import TimelineDashboard from "./Pages/TimelineDashboard";
import VBCDashboard from "./Pages/VBCDashboard";
import VBCSummary from "./Pages/VBCSummary";
import VBCWorkQueue from "./Pages/VBCWorkQueue";
import ChatbotWindow from "./components/chatbot/ChatbotWindow";
import BaaGate from "./components/baa/BaaGate";
import { loginRequest } from "./authConfig";
import setMyDetails from "./redux/me-actions";
import { store } from "./redux/store";
import { normalizeRole } from "./lib/rbac";

import { SubscriptionProvider } from "./context/SubscriptionContext";
import { TourProvider } from "./components/tour/TourProvider";
import BillingAlertBanner from "./components/billing/BillingAlertBanner";
import PaymentSuccess from "./Pages/PaymentSuccess";
import PaymentFailed from "./Pages/PaymentFailed";
import LockoutGuard from "./components/billing/LockoutGuard";

const queryClient = new QueryClient();

function decodeJwt(token) {
  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Failed to decode JWT from URL token:", error);
    return null;
  }
}

const SESSION_TOKEN_ISSUER = "seismic-backend";
const SESSION_TOKEN_AUDIENCE = "seismic-app";

function isUsableBackendSessionToken(token, expectedEmail = "") {
  const claims = decodeJwt(token);
  if (!claims) return false;

  const nowSeconds = Math.floor(Date.now() / 1000);
  if (typeof claims.exp !== "number" || claims.exp <= nowSeconds) return false;
  if (claims.iss !== SESSION_TOKEN_ISSUER) return false;

  const audience = Array.isArray(claims.aud) ? claims.aud : [claims.aud];
  if (!audience.includes(SESSION_TOKEN_AUDIENCE)) return false;

  const tokenEmail = String(claims.email || "").trim().toLowerCase();
  const identityEmail = String(expectedEmail || "").trim().toLowerCase();

  return !identityEmail || !tokenEmail || tokenEmail === identityEmail;
}

async function verifyStandaloneSession(idToken) {
  const claims = decodeJwt(idToken);
  if (!claims) {
    throw new Error("Invalid ID token");
  }

  const baseUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  const response = await fetch(`${baseUrl}/api/standalone/auth/verify`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      idToken,
      email: claims.email || claims.emails?.[0] || "",
      userId: claims.sub || claims.oid || "",
    }),
  });

  let data = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok) {
    throw new Error(data?.message || data?.error || "Failed to verify standalone session");
  }

  if (!data?.token) {
    throw new Error("Backend session token was not returned");
  }

  sessionStorage.setItem("backendToken", data.token);

  return claims;
}
function Router() {
  const queryParams = new URLSearchParams(window.location.search);
  const role = queryParams.get("role");
  const isPatientView = role === "patient";
  const appointmentsRouteChecks = [
    { required: "appointments.select_providers", level: "read" },
    { required: "appointments.add", level: "write" },
    { required: "appointments.modify", level: "write" },
    { required: "appointments.delete", level: "write" },
    { required: "appointments.patient_reports", level: "read" },
  ];
  const patientsRouteChecks = [
    { required: "patients.info", level: "read" },
  ];

  return (
    <TourProvider>
    <div className="h-screen flex flex-col md:flex-row overflow-hidden">
      {!isPatientView && <Sidebar />}
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-y-auto overflow-x-hidden bg-neutral-50 p-3 sm:p-4 lg:p-6">
          <BillingAlertBanner />
          <LockoutGuard>
            <ErrorBoundary fallback={<AppShellSkeleton />}>
              <Switch>
              <Route path="/about" component={AboutUs} />
            <Route path="/about/details" component={AboutSeismic} />
            <Route path="/connect" component={Connect} />
            <Route path="/contact" component={ContactUs} />
            <Route path="/documentation" component={Documentation} />
            <Route path="/timeline" component={TimelineDashboard} />
            <AuthorizedRoute path="/intelligence" component={IntelligenceDashboard} required="dashboard.view_appointments" level="read" />

            <AuthorizedRoute
              path="/"
              component={Dashboard}
              allow
            />
            <AuthorizedRoute
              path="/appointments"
              component={Appointments}
              checks={appointmentsRouteChecks}
            />
            <AuthorizedRoute
              path="/video-call"
              component={VideoRecorder}
              checks={[
                { required: "video_call.upcoming", level: "read" },
                { required: "video_call.history", level: "read" },
                { required: "video_call.add", level: "read" },
              ]}
            />
            <AuthorizedRoute
              path="/patients"
              component={Patients}
              checks={patientsRouteChecks}
            />
            <AuthorizedRoute
              path="/patients/:patientId"
              component={PatientReports}
              checks={patientsRouteChecks}
            />
            <AuthorizedRoute
              path="/reports"
              component={Reports}
              checks={[
                { required: "reports.billing_analytics", level: "read" },
                { required: "reports.billing_history", level: "read" },
                { required: "reports.estimated_billing", level: "read" },
              ]}
            />
            <AuthorizedRoute
              path="/reports/billing-analytics"
              component={BillingReports}
              required="reports.billing_analytics"
              level="read"
            />
            <AuthorizedRoute
              path="/billing-reports"
              component={BillingReports}
              required="reports.billing_analytics"
              level="read"
            />
            <AuthorizedRoute
              path="/reports/billing-history"
              component={BillingHistory}
              required="reports.billing_history"
              level="read"
            />
            <AuthorizedRoute
              path="/billing-history"
              component={BillingHistory}
              required="reports.billing_history"
              level="read"
            />
            <AuthorizedRoute
              path="/bill-calculation"
              component={BillCalculation}
              required="reports.estimated_billing"
              level="read"
            />
            <AuthorizedRoute
              path="/invoice/:invoiceId"
              component={InvoicePreview}
              required="reports.billing_history"
              level="read"
            />
            <AuthorizedRoute
              path="/profile-settings"
              component={ProfileSettings}
              allow
            />
            <AuthorizedRoute
              path="/settings/profile"
              component={ProfileSettings}
              allow
            />
            <AuthorizedRoute
              path="/settings"
              component={Settings}
              allow
            />
            <AuthorizedRoute
              path="/settings/ehr-integration"
              component={AthenaIntegration}
              required="settings.ehr_integration"
              level="read"
            />
            <AuthorizedRoute
              path="/athena-integration"
              component={AthenaIntegration}
              required="settings.ehr_integration"
              level="read"
            />
            <AuthorizedRoute
              path="/settings/subscription"
              component={SubscriptionPlans}
              required="settings.payment_billing"
              level="read"
            />
            <AuthorizedRoute
              path="/subscription"
              component={SubscriptionPlans}
              required="settings.payment_billing"
              level="read"
            />
            <AuthorizedRoute
              path="/settings/payment-billing"
              component={PaymentBilling}
              required="settings.payment_billing"
              level="read"
            />
            <AuthorizedRoute
              path="/payment-billing"
              component={PaymentBilling}
              required="settings.payment_billing"
              level="read"
            />
            <AuthorizedRoute
              path="/settings/payment-methods"
              component={PaymentMethodManagement}
              required="settings.payment_billing"
              level="read"
            />
            <AuthorizedRoute
              path="/payment-methods"
              component={PaymentMethodManagement}
              required="settings.payment_billing"
              level="read"
            />
            <AuthorizedRoute
              path="/payment-success"
              component={PaymentSuccess}
              required="settings.payment_billing"
              level="read"
            />
            <AuthorizedRoute
              path="/payment-failed"
              component={PaymentFailed}
              required="settings.payment_billing"
              level="read"
            />
            <AuthorizedRoute
              path="/vbc"
              component={VBCSummary}
              required="dashboard.view_appointments"
              level="read"
            />
            <AuthorizedRoute
              path="/vbc/summary"
              component={VBCSummary}
              required="dashboard.view_appointments"
              level="read"
            />
            <AuthorizedRoute
              path="/vbc/work-queue"
              component={VBCWorkQueue}
              required="dashboard.view_appointments"
              level="read"
            />
            <AuthorizedRoute
              path="/vbc/details"
              component={VBCDashboard}
              required="dashboard.view_appointments"
              level="read"
            />
            <AuthorizedRoute
              path="/vbc-dashboard"
              component={VBCSummary}
              required="dashboard.view_appointments"
              level="read"
            />
            <AuthorizedRoute
              path="/admin/rbac"
              component={RBACManagement}
              required="admin.manage_rbac"
              level="read"
            />
            <AuthorizedRoute
              path="/meeting-room/:callId"
              component={StreamVideoCoreV3}
              allow={isPatientView}
              checks={[
                { required: "appointments.join_call", level: "write" },
                { required: "patients.join_call", level: "write" },
                { required: "video_call.start", level: "write" },
              ]}
            />
            <AuthorizedRoute
              path="/post-call/:callId"
              component={PostCallDocumentation}
              required="post_call.view_all"
              level="read"
            />
            <Route component={NotFound} />
              </Switch>
            </ErrorBoundary>
          </LockoutGuard>
          <ChatbotWindow />
        </main>
      </div>
    </div>
    </TourProvider>
  );
}

function AppShellSkeleton() {
  return (
    <div className="flex min-h-screen bg-neutral-50">
      <div className="hidden md:flex flex-col w-64 bg-neutral-800 p-4 gap-4">
        <div className="h-10 w-32 bg-neutral-700 rounded animate-pulse" />
        <div className="space-y-3 mt-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-8 bg-neutral-700 rounded animate-pulse" />
          ))}
        </div>
      </div>

      <div className="flex-1 flex flex-col">
        <div className="h-14 bg-white border-b border-neutral-200 flex items-center px-6 gap-4">
          <div className="h-4 w-24 bg-neutral-200 rounded animate-pulse" />
          <div className="h-4 w-16 bg-neutral-200 rounded animate-pulse" />
          <div className="ml-auto h-8 w-8 bg-neutral-200 rounded-full animate-pulse" />
        </div>
        <div className="p-6 space-y-6">
          <div className="h-20 bg-white rounded-xl shadow-sm animate-pulse" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-40 bg-white rounded-xl shadow-sm animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function AuthorizedApp({ me }) {
  return (
    <QueryClientProvider client={queryClient}>
      <BaaGate user={me} fallback={<AppShellSkeleton />}>
        <Router />
        <Toaster />
      </BaaGate>
    </QueryClientProvider>
  );
}

function Main() {
  const isAuthenticated = useIsAuthenticated();
  const [tokenBypass, setTokenBypass] = useState(false);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [hasBootstrappedAuth, setHasBootstrappedAuth] = useState(false);
  const { instance, accounts, inProgress } = useMsal();
  const dispatch = useDispatch();
  const me = useSelector((state) => state.me.me);
  const hasAppRole = Boolean(normalizeRole(me?.role));

  async function requestProfileData() {
    setIsAuthorizing(true);
    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });

      const idToken = (response.idToken || "").trim();
      if (!idToken) {
        throw new Error("Missing CIAM ID token");
      }

      sessionStorage.setItem("authToken", idToken);

      const claims = decodeJwt(idToken) || response.idTokenClaims || {};
      const existingBackendToken = (sessionStorage.getItem("backendToken") || "").trim();
      if (!isUsableBackendSessionToken(existingBackendToken, claims.email)) {
        sessionStorage.removeItem("backendToken");
        await verifyStandaloneSession(idToken);
      }

      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/audit/user-login/${response.idTokenClaims?.email}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      });

      await dispatch(setMyDetails(claims));
    } catch (error) {
      console.error("MSAL backend session exchange failed:", error);
      sessionStorage.removeItem("backendToken");
      sessionStorage.removeItem("authToken");
      throw error;
    } finally {
      setIsAuthorizing(false);
    }
  }

  useEffect(() => {
    let isMounted = true;

    async function hydrateUser() {
      if (inProgress !== "none") {
        return;
      }

      setIsAuthorizing(true);
      try {
        const queryParams = new URLSearchParams(window.location.search);
        const tokenFromUrl = queryParams.get("token");
        const storedToken = sessionStorage.getItem("bypassToken");
        const activeToken = tokenFromUrl || storedToken;

        if (activeToken) {
          if (tokenFromUrl) {
            sessionStorage.setItem("bypassToken", tokenFromUrl);
            sessionStorage.setItem("authToken", tokenFromUrl);
          }

          try {
            const claims = await verifyStandaloneSession(activeToken);
            await dispatch(setMyDetails(claims));
            if (isMounted) {
              setTokenBypass(true);
            }
          } catch (error) {
            console.error("Standalone session verification failed:", error);
            sessionStorage.removeItem("bypassToken");
            sessionStorage.removeItem("backendToken");
          }
          return;
        }

        if (isAuthenticated) {
          try {
            await requestProfileData();
          } catch (error) {
            console.error("Doctor session bootstrap failed:", error);
            sessionStorage.removeItem("backendToken");
            sessionStorage.removeItem("authToken");
          }
        }
      } finally {
        if (isMounted) {
          setIsAuthorizing(false);
          setHasBootstrappedAuth(true);
        }
      }
    }

    hydrateUser();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProgress, isAuthenticated]);

  const accessDenied = (
    <AccessDenied
      title="Access pending"
      description="Your account is authenticated, but no completed Seismic app role is assigned yet."
    />
  );
  const registrationDenied = (
    <AccessDenied
      title="Registration incomplete"
      description="Please complete your standalone registration before opening the Seismic app."
    />
  );
  const pendingApprovalDenied = (
    <AccessDenied
      title="Approval pending"
      description="Your account is waiting for clinic administrator approval."
    />
  );
  const rejectedApprovalDenied = (
    <AccessDenied
      title="Access not approved"
      description="Your account has not been approved for Seismic app access."
    />
  );

  if (!hasBootstrappedAuth || inProgress !== "none") {
    return <AppShellSkeleton />;
  }

  return (
    <>
      {tokenBypass ? (
        isAuthorizing ? (
          <AppShellSkeleton />
        ) : me?.profileComplete !== true ? (
          registrationDenied
        ) : me?.approvalStatus === "pending" ? (
          pendingApprovalDenied
        ) : me?.approvalStatus === "rejected" ? (
          rejectedApprovalDenied
        ) : hasAppRole ? (
          <AuthorizedApp me={me} />
        ) : (
          accessDenied
        )
      ) : isAuthorizing && isAuthenticated ? (
        <AuthenticatedTemplate>
          <AppShellSkeleton />
        </AuthenticatedTemplate>
      ) : hasAppRole ? (
        <AuthenticatedTemplate>
          <AuthorizedApp me={me} />
        </AuthenticatedTemplate>
      ) : (
        <AuthenticatedTemplate>{accessDenied}</AuthenticatedTemplate>
      )}

      {!isAuthenticated && !tokenBypass && (
        <UnauthenticatedTemplate>
          <AuthPage />
        </UnauthenticatedTemplate>
      )}
    </>
  );
}


function App() {
  // Development-only synthetic preview; never exposes live data or bypasses production authentication.
  if (process.env.NODE_ENV === "development" && window.location.pathname === "/intelligence-preview") {
    return <QueryClientProvider client={queryClient}><main className="min-h-screen bg-neutral-50 p-4 md:p-8"><IntelligenceDashboard preview /></main></QueryClientProvider>;
  }
  return (
    <Provider store={store}>
      <SubscriptionProvider>
        <div className="App">
          <Main />
        </div>
      </SubscriptionProvider>
    </Provider>
  );
}

export default App;
